import React, { useCallback } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { Trans } from 'react-i18next';

import { useBreadBoard } from 'components/contexts/Toaster';
import DragOverlay from 'components/uploader/components/DragOverlay';
import ValidationErrorMessage from 'components/uploader/components/ValidationErrorMessage';

import {
  DEFAULT_MAX_SIZE,
  FILE_TYPES,
  mimeTypes,
} from 'components/helpers/files';

export default function AttachmentDropZone({
  children,
  className,
  fileTypes = FILE_TYPES,
  maxFileSize = DEFAULT_MAX_SIZE,
  maxUploads = 9,
  onUpload,
  uploadsCount = 0,
  ...restProps
}) {
  const breadBoard = useBreadBoard();

  const acceptedFileTypes = fileTypes.filter((fileType) =>
    FILE_TYPES.includes(fileType),
  );

  const acceptedMimeTypes = mimeTypes({ fileTypes: acceptedFileTypes });

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      rejectedFiles.forEach((file) => {
        breadBoard.addInedibleToast({
          fullMessage: (
            <ValidationErrorMessage
              acceptedMimeTypes={acceptedMimeTypes}
              file={file}
              maxSize={maxFileSize}
            />
          ),
        });
      });

      const isMaxUploadsReached =
        uploadsCount + acceptedFiles.length > maxUploads;

      if (isMaxUploadsReached) {
        breadBoard.addInedibleToast({
          fullMessage: (
            <Trans
              i18nKey='attachments.errors.maxUploads'
              ns='common'
              values={{ count: maxUploads }}
            />
          ),
        });
      } else {
        acceptedFiles.forEach((file) => onUpload?.(file));
      }
    },
    [acceptedMimeTypes, maxFileSize, maxUploads, onUpload, uploadsCount],
  );

  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    accept: acceptedMimeTypes,
    minSize: 0,
    maxSize: maxFileSize,
    noClick: true,
    noKeyboard: true,
    onDrop,
  });

  return (
    <div
      data-element-name='attachment-drop-zone'
      {...getRootProps({ className: classNames('tw-relative', className) })}
      {...restProps}
    >
      <input {...getInputProps()} />
      {isDragActive && <DragOverlay />}
      {children}
    </div>
  );
}

AttachmentDropZone.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  fileTypes: PropTypes.arrayOf(PropTypes.oneOf(FILE_TYPES)),
  maxFileSize: PropTypes.number,
  maxUploads: PropTypes.number,
  onUpload: PropTypes.func,
  uploadsCount: PropTypes.number,
};
